/**
 * Go-stringified-map normalizer (Task 4068a defensive safety net).
 *
 * The 4065a contract mandates `subject.args` is always a JSON object, never a
 * Go-stringified `map[...]` blob (INVARIANT 8) — so in the new model this
 * defect is structurally impossible. But during the cutover (before 4068b
 * flips the backend flag) a legacy item can still reach the renderer carrying
 * a value like:
 *
 *   "map[asc_review_id:01234567-89ab rating:1 approved:true]"
 *
 * which is `fmt.Sprintf("%v", someMap)` output from Go. Rendered verbatim this
 * is the exact prod screenshot bug the redesign set out to kill. This util is
 * the UI-side safety net: when a string matches the flat Go-map shape it is
 * parsed into a plain object so the type-aware renderer shows keyed fields;
 * otherwise the input is returned unchanged.
 *
 * Deliberately conservative — it only recognises the flat `map[k:v k2:v2]`
 * form with identifier-like keys. Nested maps / values containing spaces are
 * NOT reliably reconstructable from `%v` output, so we don't guess: anything
 * that doesn't cleanly match returns `{ matched: false }` and the caller
 * renders the original string. Better to show the raw blob (rare, legacy,
 * transient) than to fabricate a misleading parse.
 */

export interface GoMapParseResult {
  /** True when the input was a recognised Go-stringified map and was parsed. */
  matched: boolean
  /** The parsed key/value object — only meaningful when `matched` is true. */
  value: Record<string, string>
}

// `map[` ... `]` with no nested `map[`/`]` inside (flat maps only).
const GO_MAP_RE = /^map\[([^\]]*)\]$/

export function normalizeGoMapString(input: unknown): GoMapParseResult {
  if (typeof input !== 'string') {
    return { matched: false, value: {} }
  }
  const trimmed = input.trim()
  const m = GO_MAP_RE.exec(trimmed)
  if (!m) {
    return { matched: false, value: {} }
  }

  const body = m[1]
  // Empty map `map[]` → an empty object (still a recognised match: it should
  // render as "{}" / no fields rather than the literal "map[]").
  if (body.trim().length === 0) {
    return { matched: true, value: {} }
  }

  // Go prints map entries space-separated as `key:value`. We split on
  // whitespace, then on the FIRST colon of each token so values may contain
  // colons (e.g. timestamps, ids). A token without a colon, or with an empty
  // key, fails the match — we don't try to recover a partially-garbled blob.
  const out: Record<string, string> = {}
  const tokens = body.split(/\s+/).filter((t) => t.length > 0)
  for (const token of tokens) {
    const colon = token.indexOf(':')
    if (colon <= 0) {
      // No colon, or leading colon (empty key) — not a clean k:v entry.
      return { matched: false, value: {} }
    }
    const key = token.slice(0, colon)
    const val = token.slice(colon + 1)
    // Keys in real Go maps here are identifier-like (snake_case field names).
    // A key with spaces/odd chars would have been caught by the split, but
    // guard against an empty value too — `key:` with nothing after is allowed
    // (renders as empty), but a non-identifier key is rejected.
    if (!/^[A-Za-z0-9_.-]+$/.test(key)) {
      return { matched: false, value: {} }
    }
    out[key] = val
  }

  return { matched: true, value: out }
}
